'use client';
import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';

interface Campaign {
  id: string;
  brand: string;
  product: string;
  category: string;
  image_url: string;
  reward_amount: number;
  avg_rating: number;
  available_slots: number;
  marketplace_name: string;
}

const categoryColors: Record<string, { bg: string; text: string }> = {
  Electronics: { bg: '#EEF0FB', text: '#1A237E' },
  Beauty: { bg: '#FDF2F8', text: '#9D174D' },
  'Hair Care': { bg: '#F5F3FF', text: '#6D28D9' },
  Wearables: { bg: '#EFF6FF', text: '#1D4ED8' },
  'Food & Beverages': { bg: '#F0FDF4', text: '#166534' },
  Fitness: { bg: '#FFFBEB', text: '#92400E' },
  'Home & Kitchen': { bg: '#F0FDFA', text: '#134E4A' },
};

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((s) => (
        <Icon
          key={s}
          name="StarIcon"
          size={12}
          variant={s <= Math.round(rating) ? 'solid' : 'outline'}
          className={s <= Math.round(rating) ? 'star-filled' : 'text-border'}
        />
      ))}
      <span className="text-xs text-muted-foreground ml-1">{(rating || 0).toFixed(1)}</span>
    </div>
  );
}

interface FeaturedCampaignsProps {
  onReviewClick: () => void;
}

export default function FeaturedCampaigns({ onReviewClick }: FeaturedCampaignsProps) {
  const sectionRef = useRef<HTMLElement>(null);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchCampaigns() {
      const supabase = createClient();
      try {
        const { data, error } = await supabase
          .from('campaigns')
          .select('id, brand, product, category, image_url, reward_amount, avg_rating, available_slots, marketplace_name')
          .eq('campaign_status', 'active')
          .order('avg_rating', { ascending: false })
          .limit(6);

        if (error) {
          console.log('Campaigns fetch error:', error.message);
        } else {
          setCampaigns(data || []);
        }
      } catch (err) {
        console.log('Campaigns fetch error:', err);
      } finally {
        setLoading(false);
      }
    }
    fetchCampaigns();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const cards = entry.target.querySelectorAll<HTMLElement>('.campaign-card');
            cards.forEach((card, i) => {
              setTimeout(() => {
                card.style.opacity = '1';
                card.style.transform = 'translateY(0)';
              }, i * 80);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, [campaigns]);

  return (
    <section ref={sectionRef} className="section-py bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-4 mb-10">
          <div>
            <span className="pill-badge mb-3">Live Campaigns</span>
            <h2 className="font-heading font-bold text-foreground mt-3" style={{ fontSize: 'clamp(1.5rem, 3vw, 2rem)' }}>
              Featured Review Campaigns
            </h2>
            <p className="text-muted-foreground text-sm mt-1">Apply now — slots fill fast</p>
          </div>
          <Link
            href="/products"
            className="inline-flex items-center gap-2 text-sm font-semibold text-primary border border-primary/20 bg-primary-container rounded-xl px-4 py-2 hover:bg-primary/10 transition-colors shrink-0"
          >
            View All Campaigns
            <Icon name="ArrowRightIcon" size={16} className="text-primary" />
          </Link>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white rounded-2xl border border-border overflow-hidden animate-pulse">
                <div className="h-48 bg-muted" />
                <div className="p-5 space-y-3">
                  <div className="h-4 bg-muted rounded w-1/2" />
                  <div className="h-4 bg-muted rounded w-3/4" />
                  <div className="h-8 bg-muted rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : campaigns.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">No active campaigns at the moment.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {campaigns.map((campaign, i) => {
              const catStyle = categoryColors[campaign.category] || { bg: '#F7F8FA', text: '#374151' };
              return (
                <div
                  key={campaign.id}
                  className="campaign-card bg-white rounded-2xl border border-border overflow-hidden card-hover card-shine"
                  style={{
                    opacity: 0,
                    transform: 'translateY(20px)',
                    transition: `opacity 0.6s cubic-bezier(0.16,1,0.3,1), transform 0.6s cubic-bezier(0.16,1,0.3,1)`,
                    transitionDelay: `${i * 0.08}s`,
                  }}
                >
                  <div className="img-zoom h-48 w-full relative">
                    <AppImage
                      src={campaign.image_url || '/assets/images/no_image.png'}
                      alt={`${campaign.product} product image for review campaign`}
                      fill
                      className="object-cover"
                      sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                    />
                  </div>
                  <div className="p-5 space-y-3">
                    <div className="flex items-center justify-between">
                      <span
                        className="text-[10px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full"
                        style={{ backgroundColor: catStyle.bg, color: catStyle.text }}
                      >
                        {campaign.category}
                      </span>
                      <span className="text-[10px] font-medium text-muted-foreground bg-muted px-2 py-1 rounded-full">
                        {campaign.marketplace_name}
                      </span>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground font-medium">{campaign.brand}</div>
                      <h3 className="font-heading font-semibold text-foreground line-clamp-2 mt-0.5 text-sm">
                        {campaign.product}
                      </h3>
                    </div>
                    <StarRating rating={campaign.avg_rating || 0} />
                    <div className="flex items-center justify-between pt-1">
                      <div>
                        <div className="text-[10px] text-muted-foreground uppercase tracking-wide">Reward</div>
                        <div className="font-heading font-bold text-primary text-lg">
                          ₹{(campaign.reward_amount || 0).toLocaleString('en-IN')}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-[10px] text-muted-foreground uppercase tracking-wide">Slots Left</div>
                        <div
                          className={`font-heading font-semibold text-sm ${
                            (campaign.available_slots || 0) <= 5 ? 'text-red-500' : 'text-foreground'
                          }`}
                        >
                          {campaign.available_slots || 0} remaining
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={onReviewClick}
                      className="w-full cta-gradient text-white font-heading font-semibold py-2.5 rounded-xl text-sm hover:opacity-90 transition-opacity mt-1 shadow-sm shadow-primary/20"
                    >
                      Write a Review
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}